/*
 * paex-wishlist-count.js — saved-item count in the header heart (Swym).
 * PE, 2026-08-28.
 *
 * The header heart doubles as the counter: outlined while the wishlist is
 * empty, filled red with the number inside once it is not. Markup is
 * snippets/paex-wishlist-launcher.liquid, styling is the {% style %} block in
 * sections/header.liquid.
 *
 * WHY THE COUNT CANNOT BE SERVER-RENDERED
 * Shopify has no idea what is in a Swym wishlist — it lives on Swym's side,
 * keyed to a Swym session. So the number can only arrive after Swym boots
 * (~3s). The heart is therefore server-rendered in its EMPTY state and fills in
 * later. That is a colour and text change inside a fixed-size box, never a
 * layout shift, and a shopper with an empty wishlist — the common case — sees
 * the final state immediately.
 *
 * Deliberately NOT remembered between pageviews. Seeding the badge from a
 * localStorage memo would paint a number instantly, but it would be a number
 * from the last visit: wrong after any change made elsewhere, and wrong in the
 * one direction that matters (showing saved items to someone whose list Swym
 * has since emptied). snippets/paex-wishlist-app-state.liquid carries the long
 * version of why remembered Swym state has burned us before.
 *
 * WHY THIS IS A THIRD SWYM CONSUMER AND NOT A SHARED CORE
 * assets/rcc-card-components.js §1 and assets/paex-wishlist-pdp.js both own a
 * full wishlist bridge — per-variant state, click handling, un-brick timers —
 * because they render controls. This file renders a number. It needs a count
 * and two events, so it borrows the pattern rather than the machinery, and it
 * is the only one of the three that loads on EVERY page.
 */

if (!window.paexWishlistCountLoaded) {
  window.paexWishlistCountLoaded = true;

  (() => {
    const MAX = 99;
    const HAS_ITEMS = 'paex-has-items';
    const WIDE = 'header-icon--wishlist__count--wide';
    const RECONCILE_MS = 1500;
    const CALL_TIMEOUT_MS = 8000;

    /* Tri-state, like every other wishlist consumer: null means "no answer
       yet", and the badge simply stays in its empty state. Only a real number
       ever paints. */
    let count = null;
    let swat = null;
    let reconcileTimer = null;

    const launchers = () => document.querySelectorAll('.header-icon--wishlist');

    const render = () => {
      const n = typeof count === 'number' && count > 0 ? count : 0;
      const text = n > MAX ? MAX + '+' : String(n);

      launchers().forEach((link) => {
        const badge = link.querySelector('.header-icon--wishlist__count');
        link.classList.toggle(HAS_ITEMS, n > 0);

        const base = link.dataset.labelBase || link.getAttribute('aria-label') || '';
        /* "(3)" needs no translation, so the count reaches assistive tech in
           every locale without a new locale key. */
        link.setAttribute('aria-label', n > 0 ? base + ' (' + text + ')' : base);

        if (!badge) return;
        badge.textContent = n > 0 ? text : '';
        badge.classList.toggle(WIDE, text.length > 2);
      });
    };

    /* Swym's own count endpoint (/lists/wishlist/count) returns a scalar, so it
       is far cheaper than pulling the whole list — which matters because this
       runs on every page, alongside the full swat.fetch() the card and PDP
       components already make on their own pages. Falls back to counting the
       list if the method is missing or errors, so a Swym API change degrades to
       the slower path instead of losing the badge. */
    const readCount = () => {
      if (!swat) return;

      let settled = false;
      const settle = (value) => {
        if (settled) return;
        settled = true;
        if (typeof value === 'number' && value >= 0) {
          count = value;
          render();
        }
      };
      setTimeout(() => settle(null), CALL_TIMEOUT_MS);

      const viaList = () => {
        try {
          swat.fetch((items) => settle(Array.isArray(items) ? items.length : null));
        } catch (error) {
          settle(null);
        }
      };

      if (typeof swat.fetchWishlistCount !== 'function') return viaList();

      try {
        swat.fetchWishlistCount(
          (response) => {
            const value = response && response.data ? response.data.count : null;
            if (typeof value === 'number') return settle(value);
            viaList();
          },
          () => viaList()
        );
      } catch (error) {
        viaList();
      }
    };

    /* Optimistic, then reconciled. The delta paints immediately so the heart
       responds in the same frame as the click, but Swym is the authority: an
       add of something already saved, or a change made in another tab that
       replays here, would drift a locally-kept tally. The debounce collapses a
       burst of clicks into one verifying request. */
    const bump = (delta) => {
      if (typeof count === 'number') {
        count = Math.max(0, count + delta);
        render();
      }
      clearTimeout(reconcileTimer);
      reconcileTimer = setTimeout(readCount, RECONCILE_MS);
    };

    const start = (instance) => {
      if (swat) return; // SwymCallbacks can fire more than once
      swat = instance;
      readCount();
      swat.evtLayer?.addEventListener('sw:addedtowishlist', () => bump(1));
      swat.evtLayer?.addEventListener('sw:removedfromwishlist', () => bump(-1));
    };

    /* App-off: the launchers are already hidden by the :root[data-paex-wishlist-app='off']
       rule in header.liquid, but clear the number too so nothing stale is
       revealed if that verdict later flips back to unknown. */
    document.addEventListener('paex:wishlist-app', (event) => {
      if (event.detail && event.detail.enabled === false) {
        count = null;
        render();
      }
    });

    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(start);
  })();
}
